"use client";

import React, { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { Link, useRouter } from "@/i18n/routing";
import { House, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { login, LoginRequest } from "@/service/auth-service";
import { socialLogin, SocialProvider } from "@/service/social-auth-service";
import { useUserStore } from "@/store/store";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import SocialAuthButtons from "@/components/social-auth-buttons";

const loginSchema = z.object({
  email: z
    .string()
    .email("Invalid email address")
    .min(1, "Email is required")
    .max(255, "Email is too long"),
  password: z
    .string()
    .min(1, "Password is required")
    .max(255, "Password is too long"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const t = useTranslations("general");
  const { setUser, setIsAuthenticated } = useUserStore();
  const { toast } = useToast();

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Pre-fill email from URL if provided
  useEffect(() => {
    const emailFromUrl = searchParams?.get("email");
    if (emailFromUrl) {
      form.setValue("email", emailFromUrl);
    }
  }, [searchParams, form]);

  const { mutate: handleLogin, isPending } = useMutation({
    mutationFn: async (data: LoginFormData) => {
      const loginRequest: LoginRequest = {
        email: data.email,
        password: data.password,
      };
      return await login(loginRequest);
    },
    onSuccess: (response) => {
      // Store user data
      if (response.user_data) {
        setUser(response.user_data);
        setIsAuthenticated(true);
      }

      // Check for redirect parameter
      const redirectPath = searchParams?.get("redirect");
      
      // Handle navigation based on user role and profile completion
      const user = response.user_data;
      if (!user) {
        router.push(redirectPath || "/");
        return;
      }

      if (user.role_id === 2) {
        // Client - redirect to client dashboard or redirect path
        toast({
          title: "Success",
          description: "Logged in successfully",
        });
        router.push(redirectPath || "/app/client/dashboard");
      } else if (user.role_id === 3) {
        // Trainer
        if (!user.is_profile_completed) {
          // Trainer with incomplete profile → redirect to trainer wizard
          router.push("/auth/trainer/signup-wizard");
        } else {
          // Trainer with completed profile (approved or unapproved) - redirect to trainer dashboard
          if (!user.is_approved) {
            // Show pending approval message
            toast({
              title: "Pending Approval",
              description: "Your profile is pending approval. We'll notify you once approved.",
            });
          } else {
            toast({
              title: "Success",
              description: "Logged in successfully",
            });
          }
          router.push(redirectPath || "/app/trainer/dashboard");
        }
      } else {
        // Fallback to storefront or redirect path
        router.push(redirectPath || "/");
      }
    },
    onError: (error: any) => {
      console.error("Login error:", error);
      let errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Login failed. Please check your credentials.";
      
      // Handle rate limiting errors
      if (error?.response?.status === 429) {
        errorMsg = "Too many login attempts. Please wait a moment before trying again.";
      }
      
      setErrorMessage(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: LoginFormData) => {
    setErrorMessage("");
    handleLogin(data);
  };

  // Social login handler
  const { mutate: handleSocialLogin, isPending: isSocialLoginPending } = useMutation({
    mutationFn: async (provider: SocialProvider) => {
      // For login, we don't need role_id - backend will use existing user's role
      // But we need to pass a default role_id (backend requires it, but will use existing user's role)
      // Using role_id 2 (client) as default - backend will override with actual user role
      return await socialLogin(provider, 2);
    },
    onSuccess: (response) => {
      // Store user data
      if (response.user_data) {
        setUser(response.user_data);
        setIsAuthenticated(true);
      }

      // Check for redirect parameter
      const redirectPath = searchParams?.get("redirect");

      // Handle navigation based on user role and profile completion
      const user = response.user_data;
      if (!user) {
        router.push(redirectPath || "/");
        return;
      }

      if (user.role_id === 2) {
        // Client - redirect to client dashboard
        toast({
          title: "Success",
          description: "Logged in successfully",
        });
        router.push(redirectPath || "/app/client/dashboard");
      } else if (user.role_id === 3) {
        // Trainer
        if (!user.is_profile_completed) {
          // Trainer with incomplete profile → redirect to trainer wizard
          router.push("/auth/trainer/signup-wizard");
        } else {
          // Trainer with completed profile
          if (!user.is_approved) {
            toast({
              title: "Pending Approval",
              description: "Your profile is pending approval. We'll notify you once approved.",
            });
          } else {
            toast({
              title: "Success",
              description: "Logged in successfully",
            });
          }
          router.push(redirectPath || "/app/trainer/dashboard");
        }
      } else {
        // Fallback
        router.push(redirectPath || "/");
      }
    },
    onError: (error: any) => {
      console.error("Social login error:", error);
      const errorMsg =
        error?.message || "Social login failed. Please try again.";
      setErrorMessage(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    },
  });

  const handleGoogleLogin = () => {
    setErrorMessage("");
    handleSocialLogin(SocialProvider.GOOGLE);
  };

  const handleFacebookLogin = () => {
    setErrorMessage("");
    handleSocialLogin(SocialProvider.FACEBOOK);
  };

  return (
    <>
      {errorMessage && (
        <div className="bg-red-800 text-center fixed z-50 w-full p-3 flex justify-center">
          <p className="text-white uppercase text-sm font-poppins">
            {errorMessage}
          </p>
        </div>
      )}
      <div className="relative flex flex-col justify-center items-center min-h-screen bg-black-bg px-4 py-8 sm:py-12">
        {/* Background Image */}
        <Image
          src="/signin-bg.webp"
          alt="Background"
          fill
          className="absolute z-10 w-full h-full object-cover"
          priority
        />
        
        {/* Home Button */}
        <Link
          href="/"
          className={cn(
            "absolute flex justify-center items-center top-5 text-primary-foreground w-12 h-12 p-3 bg-gradient-to-t from-primary to-second-primary rounded-full z-30",
            locale === "ar" ? "right-5" : "left-5"
          )}
        >
          <House className="w-6 h-6" />
        </Link>

        {/* Login Card */}
        <Card className="mx-auto z-20 w-full max-w-md border-[#595959] bg-black-bg text-primary-foreground rounded-2xl sm:rounded-3xl pt-8 sm:pt-16 px-4 sm:px-6 pb-6 sm:pb-8">
          <CardHeader className="justify-center items-center px-2 sm:px-6">
            <div className="flex justify-center items-center mb-4 sm:mb-6">
              <Image
                src="/logo.webp"
                alt="logo"
                width={160}
                height={160}
                className="w-[120px] sm:w-[160px] object-contain"
              />
            </div>
            <CardTitle className="text-4xl sm:text-5xl lg:text-6xl font-normal">
              <h1 className="lowercase">{t("sign_in") || "sign in"}</h1>
            </CardTitle>
            <CardDescription className="text-primary-foreground font-medium text-sm sm:text-base">
              {t("login_description") || "Get access to members only content."}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            <Form {...form}>
              <form className="grid gap-4" onSubmit={form.handleSubmit(onSubmit)}>
                <div className="grid gap-2">
                  <FormField
                    name="email"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl {...field}>
                          <Input
                            id="email"
                            type="email"
                            placeholder={t("email_place_holder") || "Your email address"}
                            required
                            className="bg-black rounded-full border-[#595959] text-primary-foreground py-5 sm:py-6 text-sm sm:text-base"
                          />
                        </FormControl>
                        <FormMessage className="text-start text-red-500" />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid gap-2">
                  <FormField
                    name="password"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormControl {...field}>
                          <div className="relative">
                            <Input
                              id="password"
                              type={showPassword ? "text" : "password"}
                              placeholder={t("password_place_holder") || "Password"}
                              required
                              className="bg-black rounded-full border-[#595959] text-primary-foreground py-5 sm:py-6 pr-10 sm:pr-12 text-sm sm:text-base"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                              {showPassword ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage className="text-start text-red-500" />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex justify-end">
                  <Link
                    href="/auth/forgot-password"
                    className="text-sm text-primary-foreground/70 hover:text-primary-foreground underline transition-colors"
                  >
                    {t("forgot_password") || "Forgot password?"}
                  </Link>
                </div>
                <Button
                  type="submit"
                  className="w-full font-bebas rounded-full bg-gradient-to-r from-primary to-second-primary text-2xl sm:text-3xl py-5 sm:py-6 sm:pt-7"
                  disabled={isPending || isSocialLoginPending}
                >
                  {isPending ? (t("loading") || "Loading...") : (t("sign_in") || "sign in")}
                </Button>

                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-[#595959]" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-black-bg px-2 text-primary-foreground/70">
                      {t("or") || "Or"}
                    </span>
                  </div>
                </div>

                {/* Social Auth Buttons */}
                <SocialAuthButtons
                  onGoogleClick={handleGoogleLogin}
                  onFacebookClick={handleFacebookLogin}
                  isLoading={isSocialLoginPending}
                  disabled={isPending}
                />
              </form>
            </Form>

            <div className="mt-6 pt-5 border-t border-[#595959]/60 text-center px-1">
              <p className="text-sm text-primary-foreground/70 font-poppins leading-relaxed">
                <span>{t("dont_have_account")}</span>
                {" "}
                <Link
                  href="/auth/signup"
                  className="text-primary font-semibold hover:underline underline-offset-2"
                >
                  {t("sign_up")}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
